/**
 * Things lying on the ground.
 *
 * Until now a broken block teleported into the bag, and breaking was *refused*
 * when the bag was full - the only honest option, since anything that did not
 * fit would have ceased to exist. That refusal is gone: what comes out of a
 * block now falls at your feet, and a full bag means a pile on the floor
 * rather than a block you are not allowed to mine.
 *
 * Pure physics and pure arithmetic, like the arrows. A pickup falls, lands,
 * drifts toward whoever is near, and goes into their bag - and none of that
 * needs to know what a mesh is.
 */

import { Inventory, MAX_STACK, type ItemStack } from "../inventory/inventory.js";
import { PLAYER_HEIGHT } from "../player/controller.js";

/** Blocks per second squared. Brisker than a player: this is a dropped thing. */
export const PICKUP_GRAVITY = 22;
/** How wide a dropped thing is, for resting on the ground. */
export const PICKUP_SIZE = 0.3;
/**
 * Close enough to go in the bag, measured from the body rather than a point.
 *
 * "Зараз щоб підібрати речі чи блоки треба зайти майже на них." And so you
 * did: the distance was taken from a single point at chest height, so a
 * thing lying on the floor was already nine tenths of a block away before
 * anybody had moved - and the one and two fifths left about a block of
 * actual reach, less if it had come to rest on a step below you.
 *
 * The player is a column, not a point. Anything level with any part of that
 * column counts as being at nought height, and what is left is a plain
 * horizontal distance - so this number now means what it says: a block and a
 * fifth, at your feet or at your head, all round you.
 */
export const PICKUP_REACH = 1.2;
/** Close enough to start sliding toward you. */
export const PICKUP_MAGNET = 3.4;
/**
 * How long before it can be picked up, in seconds.
 *
 * Without it, everything you drop deliberately is back in your hand on the
 * same frame, and the drop key does nothing at all.
 */
export const PICKUP_DELAY = 0.6;
/** How long a thing lies there before it is gone, in seconds. */
export const PICKUP_LIFETIME = 300;

/**
 * How long *your own bag* lies there, in seconds.
 *
 * Longer than a stray cobble, because it is not one. You die where the fight
 * was, you wake where your bed is, and the walk back is the whole of the
 * punishment - so the clock that matters is not "how long does a dropped
 * thing last" but "how long does it take to get there and back". Five minutes
 * is the first of those; ten is nearer the second.
 */
export const DEATH_LIFETIME = 600;

export interface Pickup {
  readonly id: number;
  stack: ItemStack;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** Seconds still to wait before anyone may pick it up. */
  delay: number;
  /** Seconds it has been lying about. */
  age: number;
  /** Seconds it is allowed to lie about. See DEATH_LIFETIME. */
  keepFor: number;
}

let nextId = 1;

/**
 * Drop a stack into the world.
 *
 * `spread` throws it in a direction; a block coming apart uses a small random
 * one so a handful of drops does not stack into a single point, and the drop
 * key uses the direction the player is looking.
 */
export function makePickup(
  stack: ItemStack,
  at: { x: number; y: number; z: number },
  spread: { x: number; y: number; z: number } = { x: 0, y: 0, z: 0 },
  delay = PICKUP_DELAY,
  keepFor = PICKUP_LIFETIME,
): Pickup {
  return {
    id: nextId++,
    stack: { ...stack },
    x: at.x, y: at.y, z: at.z,
    vx: spread.x, vy: spread.y, vz: spread.z,
    delay,
    age: 0,
    keepFor,
  };
}

/**
 * Where a thing dropped here would actually come to rest.
 *
 * Nothing holds a dropped thing up but the ground, and the world reads as air
 * outside itself - so a bag spilled over a hole, or off the edge of the map,
 * falls for ever. It is still "lying there" as far as the clock is concerned,
 * and it is gone as far as anybody is concerned, which is the worst of both.
 *
 * @returns the cell to drop into, or null if there is nothing below at all.
 */
export function restingCell(
  cell: { x: number; y: number; z: number },
  isSolid: (x: number, y: number, z: number) => boolean,
  floorY = 0,
): { x: number; y: number; z: number } | null {
  for (let y = cell.y; y >= floorY; y--) {
    if (isSolid(cell.x, y, cell.z)) return { x: cell.x, y: y + 1, z: cell.z };
  }
  return null;
}

/**
 * Scatter a block's drops from the middle of its cell.
 *
 * Deterministic direction per index rather than random, so the same break
 * always throws the same way - one less thing that cannot be reproduced when
 * something looks wrong.
 */
export function scatterDrops(
  drops: readonly ItemStack[],
  cell: { x: number; y: number; z: number },
  keepFor = PICKUP_LIFETIME,
): Pickup[] {
  /*
   * Nothing is dropped for a count of nought.
   *
   * A кіт and a собака yield nothing when they go down - deliberately - and
   * that was written as `min: 0, max: 0`, which rolls a stack of *no* сало.
   * It landed on the ground as a real pickup that could never be picked up,
   * because taking nothing of something adds nothing to a bag. Reported as
   * "м'ясо яке падає з котів або собак не береться в інвентар", and it was
   * not meat: it was the absence of meat, drawn.
   */
  const real = drops.filter((stack) => stack.count > 0);
  return real.map((stack, i) => {
    const angle = (i / Math.max(1, real.length)) * Math.PI * 2;
    return makePickup(
      stack,
      { x: cell.x + 0.5, y: cell.y + 0.35, z: cell.z + 0.5 },
      { x: Math.cos(angle) * 1.1, y: 2.2, z: Math.sin(angle) * 1.1 },
      PICKUP_DELAY,
      keepFor,
    );
  });
}

/**
 * Advance everything on the ground, and collect what the player reaches.
 *
 * @returns how many individual items went into the bag.
 */
export function stepPickups(
  list: Pickup[],
  dt: number,
  isSolid: (x: number, y: number, z: number) => boolean,
  collector: { x: number; y: number; z: number } | null,
  bag: Inventory,
): number {
  let collected = 0;

  for (let i = list.length - 1; i >= 0; i--) {
    const item = list[i]!;
    item.age += dt;
    if (item.delay > 0) item.delay = Math.max(0, item.delay - dt);

    if (item.age > item.keepFor) {
      list.splice(i, 1);
      continue;
    }

    // --- falling ---------------------------------------------------------
    item.vy -= PICKUP_GRAVITY * dt;
    const nextY = item.y + item.vy * dt;

    // Only the floor is solid to a dropped thing. Full collision against walls
    // would be a physics engine for something that spends its life resting on
    // one block, and a thing nudged into a wall simply stops there instead.
    const floor = Math.floor(nextY - PICKUP_SIZE / 2);
    if (item.vy < 0 && isSolid(Math.floor(item.x), floor, Math.floor(item.z))) {
      item.y = floor + 1 + PICKUP_SIZE / 2;
      item.vy = 0;
      // Friction, so a thrown thing comes to rest rather than sliding away.
      item.vx *= 0.72;
      item.vz *= 0.72;
    } else {
      item.y = nextY;
    }

    item.x += item.vx * dt;
    item.z += item.vz * dt;

    // --- being collected -------------------------------------------------
    if (collector === null || item.delay > 0) continue;

    const dx = collector.x - item.x;
    const dz = collector.z - item.z;
    // Nought while it is beside you anywhere up your height; the overshoot
    // only counts once it is above your head or below your feet.
    const dy = item.y < collector.y ? collector.y - item.y
      : item.y > collector.y + PLAYER_HEIGHT
        ? item.y - (collector.y + PLAYER_HEIGHT)
        : 0;
    /*
     * A cylinder round the body, not a ball round a point.
     *
     * The two are measured apart on purpose. A thing on the step below you,
     * one block out, is one and two fifths away as the crow flies and would
     * miss a ball of one and a fifth - and a thing that has rolled off a
     * ledge is exactly the case that was worst before any of this.
     */
    const flat = Math.hypot(dx, dz);
    const distance = Math.max(flat, dy);

    if (distance < PICKUP_REACH) {
      // A partial take is the point of returning a number: a bag with room for
      // three of a stack of ten keeps the other seven on the floor rather than
      // swallowing the lot or refusing all of it.
      const left = bag.add(item.stack.id, item.stack.count);
      collected += item.stack.count - left;
      if (left <= 0) list.splice(i, 1);
      else item.stack.count = left;
      continue;
    }

    if (distance < PICKUP_MAGNET && distance > 0.001) {
      // Pulled harder the closer it is, so the last half-block is quick and
      // the thing does not orbit your feet.
      const pull = (1 - distance / PICKUP_MAGNET) * 26 * dt;
      item.vx += (dx / distance) * pull;
      item.vy += (dy / distance) * pull;
      item.vz += (dz / distance) * pull;
    }
  }

  return collected;
}

/** Merge stacks lying on top of each other, so a mined seam is not a hundred entities. */
export function mergePickups(list: Pickup[], radius = 0.6): void {
  for (let i = 0; i < list.length; i++) {
    const a = list[i]!;
    if (a.stack.count >= MAX_STACK) continue;
    for (let j = list.length - 1; j > i; j--) {
      const b = list[j]!;
      if (b.stack.id !== a.stack.id) continue;
      if (Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z) > radius) continue;
      const room = MAX_STACK - a.stack.count;
      const moved = Math.min(room, b.stack.count);
      a.stack.count += moved;
      b.stack.count -= moved;
      if (b.stack.count <= 0) list.splice(j, 1);
      if (a.stack.count >= MAX_STACK) break;
    }
  }
}
